/**
 * VIP/Pro Phase 6 checkout tracking (plan section 7): the offer details a
 * VIP or Pro checkout_started (and the native trial_started /
 * subscription_purchase client events) carry, so the RevenueCat webhook can
 * recover them for a native purchase from the member's latest
 * checkout_started row (revenuecat-webhook-handler/checkoutContext.ts).
 *
 * Legacy offer kinds get nothing: their payloads stay byte-identical.
 */
import { Capacitor } from '@capacitor/core';
import { isVipProOfferKind, vipProPlanTier } from '@/domain/subscription/pricing';

export interface VipProCheckoutDetailsInput {
  offerKind: unknown;
  /** The checkout tier: 'monthly' or 'annual' for VIP and Pro. */
  interval: string | null | undefined;
  offerVersion?: string | null;
  entryRoute?: string | null;
  /** The store or server quote the member saw, in currency units (49.99). */
  quotedPrice?: { value: number; currency: string } | null;
  /**
   * Native only: the store product being bought. Left out of the payload
   * when undefined (web checkouts); null records that it could not be
   * resolved.
   */
  storeProductId?: string | null;
}

/** Event properties for a VIP or Pro checkout; an empty object for every other offer kind. */
export function buildVipProCheckoutDetails(input: VipProCheckoutDetailsInput): Record<string, unknown> {
  if (!isVipProOfferKind(input.offerKind)) return {};
  const interval = input.interval === 'monthly' || input.interval === 'annual' ? input.interval : null;
  const quote = input.quotedPrice;
  const hasPrice = quote != null && typeof quote.value === 'number' && Number.isFinite(quote.value);
  const currency = quote && typeof quote.currency === 'string' && quote.currency.trim()
    ? quote.currency.trim().toUpperCase()
    : null;
  return {
    offer_version: input.offerVersion || null,
    entry_route: input.entryRoute || null,
    plan_tier: vipProPlanTier(input.offerKind),
    interval,
    // Integer minor units (cents), from the quote.
    displayed_price: hasPrice ? Math.round(quote.value * 100) : null,
    displayed_currency: currency,
    rescue_exposed: input.offerKind === 'vip_rescue_annual_trial',
    ...(input.storeProductId !== undefined ? { store_product_id: input.storeProductId } : {}),
  };
}

/**
 * The App Store or Play product a native VIP or Pro checkout buys. Null for
 * legacy offer kinds, on web, or when the mapping cannot load; measurement
 * never blocks the purchase.
 */
export async function resolveNativeVipProStoreProductId(
  offerKind: unknown,
  interval: string | null | undefined,
): Promise<string | null> {
  if (!isVipProOfferKind(offerKind)) return null;
  if (interval !== 'monthly' && interval !== 'annual') return null;
  const platform = Capacitor.getPlatform();
  if (platform !== 'ios' && platform !== 'android') return null;
  try {
    const { nativeVipProCheckoutProductId } = await import('@/services/nativeVipPro');
    return nativeVipProCheckoutProductId(platform, offerKind, interval);
  } catch {
    return null;
  }
}
